/**
 * O que as telas de fila compartilham e não é componente: os cartões de cada régua, os
 * rótulos dos recortes, a miniatura e a frase de situação.
 *
 * Mora fora das telas por dois motivos. Os testes leem daqui a mesma lista de cartões que a
 * tela desenha — um teste com a sua própria lista confere a lista dele, não a da tela. E o
 * *fast refresh* do Vite só recarrega arquivo que exporta apenas componentes.
 */
import type { ReactNode } from "react";

import { imagemDaAnalise } from "../api/tipos";
import type { ProximosPrazos } from "../api/tipos";
import { Icone } from "../componentes/icones";
import { restante } from "../formato";
import { agoraDoInquilino } from "../relogio";

export interface CartaoDaRegua {
  chave: string;
  rotulo: string;
  icone: ReactNode;
  severidade?: "positivo" | "atencao" | "critico";
}

/** Como cada recorte do servidor se chama para quem chegou aqui por Indicadores. Espelha
 *  `consulta.RECORTES`; sem entrada, a tela mostra o nome cru — feio, mas legível. */
export const ROTULO_DO_RECORTE: Record<string, string> = {
  todos: "Todos os trânsitos do período",
  fora_do_sla: "Não enviados em até 24h da passagem",
  dentro_do_sla: "Enviados em até 24h da passagem",
  aceitos: "Aceitos pela SENATRAN",
  recusados: "Enviados e não aceitos",
  enviados: "Com pelo menos um envio",
  cancelados_por_prazo: "Cancelados pelo prazo de 7 dias",
  pago_por_tag: "Pagos por TAG",
  pago_no_portal: "Pagos no portal",
  pago_pela_osa: "Pagos pela OSA",
  baixa_manual: "Baixados por operador",
  aguardando_pagamento: "Aguardando pagamento",
  sem_placa: "Sem placa lida",
  divergentes: "Divergência de placa não resolvida",
  nao_enviaveis: "Não enviáveis: 24h vencidas",
  venceram_hoje: "Perderam o prazo de 24 h hoje",
  // A classe econômica (`OB-01`) e o fechamento (`OB-06`): o rótulo da linha que abriu a fila.
  validas: "Passagens válidas",
  validas_nao_pagas: "Válidas ainda não pagas",
  validas_nunca_cobradas: "Válidas que ninguém cobrou",
  fraudulentas: "Inválidas fraudulentas",
  invalidas: "Inválidas: sem placa e sem volta",
  em_apuracao: "Em apuração: sem placa e ainda capturadas",
  isentos: "Isentos",
  sem_pagamento: "Sem pagamento: as evasões do período",
  // A tela de Prazos (`C-15`): os mesmos rótulos do servidor (`consulta.RECORTES`).
  em_risco: "Receita em risco: correm contra as 24 h ou os 7 dias",
  vencem_em_24h: "Vencem nas próximas 24 horas",
  cancelados_hoje: "Cancelados hoje pelos 7 dias do processamento",
  risco_24h: "Correm contra as 24 h do envio",
  risco_24h_critico: "Vencem as 24 h do envio em menos de 4 horas",
  risco_7d: "Correm contra os 7 dias do processamento",
  risco_7d_critico: "A SENATRAN cancela em menos de 1 dia",
  debitos_no_prazo: "Débitos no prazo de pagamento",
  debitos_no_prazo_critico:
    "Débitos cujo limite de pagamento vence em menos de 1 dia",
  // A prova da notificação (`OB-08`): os mesmos rótulos do servidor.
  nao_pagas_sob_protocolo: "Válidas não pagas sob o protocolo de notificação",
  nao_pagas_com_prova: "Válidas não pagas com a prova do 8º e do 15º dia",
  nao_pagas_sem_prova: "Válidas não pagas ainda sem a prova do 8º e do 15º dia",
  notificar_hoje:
    "Hoje é o 8º ou o 15º dia, e a notificação ainda não foi registrada",
  notificacao_em_falta:
    "O 8º ou o 15º dia passou sem a notificação registrada (180 dias)",
  notificacao_faltou_ontem:
    "O 8º ou o 15º dia acabou ontem sem a notificação registrada",
};

/** De onde veio o recorte, quando não foi de Indicadores. É o selo da faixa que a SENATRAN
 *  mostra em cima da tabela: "recorte de Indicadores" para quem clicou num cartão da
 *  Operação seria mandar a pessoa procurar a origem no lugar errado. */
export const ORIGEM_DO_RECORTE: Record<string, string> = {
  venceram_hoje: "perda do dia",
  em_risco: "recorte de Prazos",
  vencem_em_24h: "recorte de Prazos",
  cancelados_hoje: "recorte de Prazos",
  risco_24h: "recorte de Prazos",
  risco_24h_critico: "recorte de Prazos",
  risco_7d: "recorte de Prazos",
  risco_7d_critico: "recorte de Prazos",
  debitos_no_prazo: "recorte de Prazos",
  debitos_no_prazo_critico: "recorte de Prazos",
  notificar_hoje: "prova da notificação",
  notificacao_em_falta: "prova da notificação",
  notificacao_faltou_ontem: "prova da notificação",
};

/**
 * O que o cartão de prazo crítico diz embaixo do número — na SENATRAN e, desde 05/10, na
 * Operação. Com zero, o próximo vencimento ("0" sozinho parecia cartão quebrado); com
 * número, quando vence o primeiro da fila. `undefined` para cartão que não é de prazo, ou
 * enquanto a resposta não chegou.
 */
export function detalheDoPrazo(
  chave: string,
  contagem: number | null,
  proximos: ProximosPrazos | null,
  agora: Date = agoraDoInquilino(),
): string | undefined {
  const vence =
    chave === "prazo_24h_critico"
      ? proximos?.envio_24h
      : chave === "prazo_7d_critico"
        ? proximos?.processamento_7d
        : undefined;
  if (vence === undefined || proximos === null) return undefined;
  if (!vence) return "nenhum prazo correndo";
  const falta = restante(vence, agora);
  // Curto de propósito: o cartão compacto não quebra linha (25/09).
  if (falta === "vencido" || falta === "0m") return "1º vence agora";
  return contagem ? `1º vence em ${falta}` : `próximo vence em ${falta}`;
}

/** Os filtros do mockup (Todos, Pendentes, Divergência, Isento), mais o que espera outra
 *  pessoa e o que está para vencer. Seis, que é o teto do §3. */
export const CARTOES_DA_OPERACAO: CartaoDaRegua[] = [
  {
    chave: "na_operacao",
    rotulo: "Na fila",
    icone: <Icone.Lista />,
  },
  {
    chave: "pendentes_validacao",
    rotulo: "Pendentes",
    icone: <Icone.Relogio />,
  },
  {
    chave: "divergentes",
    rotulo: "Divergência de placa",
    icone: <Icone.Alerta />,
    severidade: "atencao",
  },
  {
    chave: "isencao_sugerida",
    rotulo: "Isenção sugerida",
    icone: <Icone.Escudo />,
  },
  // Dois cartões, e não um (Gisele, 25/09): "quando indico para revisão, o registro cai no
  // limbo". Revisão é a segunda opinião do supervisor; backoffice é o caso que sai da
  // operação (cadastro, contestação, jurídico). Juntos, quem procurava um não achava.
  {
    chave: "em_revisao",
    rotulo: "Em revisão",
    icone: <Icone.Pessoas />,
    severidade: "atencao",
  },
  {
    chave: "no_backoffice",
    rotulo: "No backoffice",
    icone: <Icone.Pessoas />,
    severidade: "atencao",
  },
  {
    chave: "prazo_24h_critico",
    rotulo: "Vencem 24 h em < 4 h",
    icone: <Icone.Relogio />,
    severidade: "critico",
  },
];

/** A miniatura que dá para reconhecer o veículo sem abrir: a frontal do pórtico, ou a foto
 *  que o cliente enviou — **sempre a cópia anonimizada** (feedback de 21/09: a equipe viu o
 *  condutor na miniatura). Sem cópia anônima, a rota responde 409 e a tela mostra a moldura
 *  vazia, nunca o original. */
export function miniaturaDe(t: {
  referencia_externa: string;
  imagem_frontal_id: string | null;
  imagem_analise_id?: string | null;
}): string | null {
  const analise = t.imagem_analise_id ?? imagemDaAnalise(t);
  // A miniatura, e não a cópia inteira (tarefa 20): uma foto de celular de 4000 px para um
  // quadro de 64 px, cinquenta por página. 160 px cobre a tela de densidade dupla.
  if (analise) return `/analise/imagens/${analise}/anonimizada?largura=160`;
  return fotoDoVisorDe(t);
}

/**
 * A foto do cabeçalho para o poder concedente (`PC-05`): só a cópia anonimizada da imagem de
 * pista, que existe para a fraude confirmada. A foto de pista e a do upload não chegam a ele
 * — o servidor já as tirou do detalhe.
 */
export function fotoDoRegulador(detalhe: {
  leituras: { imagem_id?: string | null; disponivel: boolean }[];
}): string | null {
  const copia = detalhe.leituras.find((l) => l.disponivel && l.imagem_id);
  return copia ? `/tollvision/imagens/${copia.imagem_id}/anonimizada` : null;
}

/** A foto inteira que o visor da Conferência vai pedir — a que vale pré-carregar. */
export function fotoDoVisorDe(t: {
  referencia_externa: string;
  imagem_frontal_id: string | null;
  imagem_analise_id?: string | null;
}): string | null {
  const analise = t.imagem_analise_id ?? imagemDaAnalise(t);
  if (analise) return `/analise/imagens/${analise}/anonimizada`;
  return t.imagem_frontal_id
    ? `/tollvision/imagens/${t.imagem_frontal_id}`
    : null;
}

export const CARTOES_DA_SENATRAN: CartaoDaRegua[] = [
  {
    chave: "aguardando_envio",
    rotulo: "Aguardando envio",
    icone: <Icone.Relogio />,
  },
  {
    // Conta as **tentativas** de hoje, aceitas ou recusadas (tarefa 8): o operador que
    // enviou e viu a recusa vê o cartão mudar. Por isso sem o verde de "deu certo".
    chave: "enviados_hoje",
    rotulo: "Enviados hoje",
    icone: <Icone.Enviar />,
  },
  {
    // Tudo o que saiu sem erro e ainda não teve desfecho — recebido esperando o registro, ou
    // registrado em processamento (Gisele, 25/09). O "Em processamento" saiu da régua e
    // ficou como aba da tabela: era a mesma pergunta contada duas vezes.
    chave: "aguardando_retorno",
    rotulo: "Aguardando retorno",
    icone: <Icone.Relogio />,
  },
  {
    chave: "prazo_24h_critico",
    rotulo: "Prazo crítico 24 h",
    icone: <Icone.Alerta />,
    severidade: "critico",
  },
  {
    chave: "prazo_7d_critico",
    rotulo: "Prazo crítico 7 dias",
    icone: <Icone.Alerta />,
    severidade: "critico",
  },
  {
    chave: "com_erro_de_envio",
    rotulo: "Recusados / com erro",
    icone: <Icone.Barrado />,
    severidade: "atencao",
  },
  { chave: "cancelados", rotulo: "Cancelados", icone: <Icone.Barrado /> },
];
